import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2 } from 'lucide-react';
import { EDIT_REASONS } from './workbenchShared';
import { ProjectOptions, isOutsideProjectDates } from './ProjectOptions';

// Add Session Dialog (Feb 2026) — for workers who forgot to clock in/out for a
// whole day. Admin types the date + start time + hours and optionally tags a
// project/job.
export const AddSessionDialog = ({
  worker, onClose, form, setForm, onSubmit, saving,
  projects, projectsLoading, projectsError, onRetryProjects, recentProjectIds,
  jobsCatalog, jobsLoading,
}) => (
  <Dialog open={!!worker} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-lg" data-testid="pw-add-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
      <DialogHeader>
        <DialogTitle>Add a session</DialogTitle>
        <DialogDescription>
          Create a missing time entry for <b>{worker?.worker_name}</b>.
          Useful when a worker forgot to clock in for the day.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Date *</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
              data-testid="pw-add-date"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Start time *</Label>
            <Input
              type="time"
              value={form.start_time}
              onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
              data-testid="pw-add-start"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Hours *</Label>
            <Input
              type="number"
              min="0.25"
              max="24"
              step="0.25"
              value={form.hours}
              onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
              data-testid="pw-add-hours"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Project</Label>
          <Select
            value={form.project_id || '__none__'}
            onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}
          >
            <SelectTrigger data-testid="pw-add-project-select">
              <SelectValue placeholder={projectsLoading ? 'Loading projects…' : 'No project'} />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="__none__" data-testid="pw-add-project-none">No project</SelectItem>
              <ProjectOptions
                projects={projects}
                projectsLoading={projectsLoading}
                projectsError={projectsError}
                onRetry={onRetryProjects}
                recentIds={recentProjectIds}
                testPrefix="pw-add-project"
                forDate={form.date}
              />
            </SelectContent>
          </Select>
          {(() => { const p = (projects || []).find((x) => x.id === form.project_id); return isOutsideProjectDates(p, form.date) ? (
            <p className="text-[11px] text-amber-400" data-testid="pw-add-outside-dates">
              {form.date} is outside this project's dates ({p.start_date || '…'} → {p.end_date || '…'}). The session will be saved and flagged.
            </p>
          ) : null; })()}
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Job</Label>
          <Select
            value={form.job_id || '__none__'}
            onValueChange={(v) => setForm((f) => ({ ...f, job_id: v }))}
          >
            <SelectTrigger data-testid="pw-add-job-select">
              <SelectValue placeholder={jobsLoading ? 'Loading jobs…' : 'No job'} />
            </SelectTrigger>
            <SelectContent className="max-h-[260px]">
              <SelectItem value="__none__" data-testid="pw-add-job-none">No job</SelectItem>
              {(jobsCatalog || []).map((j) => (
                <SelectItem key={j.id} value={j.id} data-testid={`pw-add-job-${j.id}`}>
                  {j.job_number ? `${j.job_number} · ` : ''}{j.title}
                  {j.status && j.status !== 'completed' && (
                    <span className="ml-1 text-[10px] opacity-60">[{j.status}]</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Reason (audit) *</Label>
          <Select
            value={form.reason_kind}
            onValueChange={(v) => setForm((f) => ({ ...f, reason_kind: v }))}
          >
            <SelectTrigger data-testid="pw-add-reason-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EDIT_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Notes (optional)</Label>
          <Textarea
            rows={2}
            placeholder="Anything else HR should see…"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            data-testid="pw-add-notes"
          />
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} data-testid="pw-add-cancel">Cancel</Button>
        <Button
          onClick={onSubmit}
          disabled={saving || !form.date || !form.start_time || !form.hours}
          className="bg-[#1E88E5] hover:bg-blue-700"
          data-testid="pw-add-save"
        >
          {saving
            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Adding…</>
            : <>Add session</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
